import {createFeatureSelector, createSelector} from '@ngrx/store';
import {DeviceState, FEATURE_NAME} from './device.state';
import {DeviceType} from '@birco/rainmesh-api';
import {selectCisternBySensorId} from '../../cisterns/state/cistern.selectors';
import {isDeviceActive} from '../utils/device-value.utils';

export const selectDeviceState = createFeatureSelector<DeviceState>(FEATURE_NAME);

export const selectFilteredDevicesWithCurrentPage = createSelector(
  selectDeviceState,
  (state: DeviceState) => {
    if (state.filter?.page === null
      || state.filter?.page === undefined
      || !state.pages[state.filter!!.page!!]) {
      return [];
    }

    return state.pages[state.filter!!.page!!]
      .map((id) => state.entities[id]) ?? []
  }
);

export const selectIsDevicesLoading = createSelector(
  selectDeviceState,
  (state) => state.isLoading
);

export const selectDeviceFilter = createSelector(
  selectDeviceState,
  (state) => state.filter
);

export const selectDevicesTotalCount = createSelector(
  selectDeviceState,
  (state) => state.totalCount
);

export const selectDeviceById = (id: string) => createSelector(
  selectDeviceState,
  (state: DeviceState) => state.entities.hasOwnProperty(id)
    ? state.entities[id]
    : null
);

export const selectIsDeviceUsableInRule = (id: string) => createSelector(
  selectDeviceById(id),
  selectCisternBySensorId(id),
  (device, matchingCistern) =>
    !!device?.isActuator
    || !!matchingCistern,
);

export const selectAllDevices = createSelector(
  selectDeviceState,
  (state: DeviceState) => Object.values(state.entities)
);

export const selectDevicesByType = (type: DeviceType) => createSelector(
  selectDeviceState,
  (state: DeviceState) => Object.values(state.entities).filter((device) => device.type === type)
);

export const selectActiveDevices = createSelector(
  selectDeviceState,
  (state) => Object.values(state.entities).filter((device) => device.isActuator && isDeviceActive(device))
);

export const selectFavoriteDevices = createSelector(
  selectDeviceState,
  (state) =>
    Object.values(state.entities)
      .filter((device) => device.isFavorite)
      .sort((a, b) => a.name.localeCompare(b.name))
);

export const selectDeviceByGroup = (groupId: string) => createSelector(
  selectDeviceState,
  (state) =>
    Object.values(state.entities)
      .filter((device) => device.deviceGroupIds?.includes(groupId))
      .sort((a, b) => a.name.localeCompare(b.name))
)

export const selectLatestDevices = createSelector(
  selectDeviceState,
  (state) => Object.values(state.entities)
    .sort((a, b) => {
        const dateA = a.lastSeenDate ? new Date(a.lastSeenDate).getTime() : 0;
        const dateB = b.lastSeenDate ? new Date(b.lastSeenDate).getTime() : 0;
        return dateB - dateA;
      }
    )
    .slice(0, 12)
);
